import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../contexts/UserContext';
import { detectBrowserPreferences } from '../../utils/browserDetection';
import api from '../../services/api';
import SubscriptionStep from './SubscriptionStep';
import PersonalInfoStep from './PersonalInfoStep';
import PreferencesStep from './PreferencesStep';
import CompletionStep from './CompletionStep';

const STEPS = ['subscription', 'personal', 'preferences', 'complete'];

function OnboardingWizard({ open, mode = 'new-user' }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { userData, refreshUserData } = useUser();

  // Determine starting step based on mode
  const startingStep = mode === 'profile-completion' ? 1 : 0;

  const [activeStep, setActiveStep] = useState(startingStep);
  const [formData, setFormData] = useState({
    // Subscription
    subscriptionChoice: 'free',

    // Personal info
    birthYear: '',
    diabetesDiagnosisYear: '',
    diabetesType: 'type1',
    country: '',

    // Preferences - defaults for Sweden-only release
    timezone: '',
    preferredUnit: 'mmol',
    language: 'sv',
  });

  // New state for wizard footer control
  const [validationState, setValidationState] = useState({ isValid: false });
  const [loading, setLoading] = useState(false);
  const [completionSuccess, setCompletionSuccess] = useState(false);
  const [completionError, setCompletionError] = useState(null);

  // Initialize with browser detection and existing user data
  useEffect(() => {
    if (open) {
      const browserPrefs = detectBrowserPreferences();

      setFormData(prev => ({
        ...prev,
        // Use existing user data if available, otherwise use browser detection
        // Default to Stockholm timezone and Swedish language for Sweden-only release
        timezone: userData?.timezone || browserPrefs.timezone || 'Europe/Stockholm',
        country: userData?.country || browserPrefs.country || '',
        language: userData?.language || browserPrefs.language || 'sv',
        preferredUnit: userData?.preferredUnit || 'mmol',
        // For existing users completing profile
        birthYear: userData?.birthYear || '',
        diabetesDiagnosisYear: userData?.diabetesDiagnosisYear || '',
        diabetesType: userData?.diabetesType || 'type1',
      }));

      // Check for returning from Stripe checkout
      const storedData = sessionStorage.getItem('onboardingData');
      if (storedData) {
        try {
          const parsed = JSON.parse(storedData);
          setFormData(prev => ({ ...prev, ...parsed }));
          // Check if returning from successful checkout
          const urlParams = new URLSearchParams(window.location.search);
          if (urlParams.get('success') === 'true') {
            // User completed Stripe payment, move to personal info step
            setActiveStep(1);
            sessionStorage.removeItem('onboardingData');
            // Clean up URL
            window.history.replaceState({}, '', window.location.pathname);
          } else if (urlParams.get('canceled') === 'true') {
            // User canceled, stay on subscription step
            setActiveStep(0);
            sessionStorage.removeItem('onboardingData');
            window.history.replaceState({}, '', window.location.pathname);
          }
        } catch (e) {
          console.error('Failed to parse stored onboarding data:', e);
        }
      }

      // Set starting step for profile completion mode
      if (mode === 'profile-completion') {
        setActiveStep(1);
      }
    }
  }, [open, userData, mode]);

  const handleNext = () => {
    setActiveStep(prev => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setActiveStep(prev => Math.max(prev - 1, mode === 'profile-completion' ? 1 : 0));
  };

  const updateFormData = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const handleComplete = async () => {
    await refreshUserData();
  };

  const handleCompletionSubmit = async () => {
    setLoading(true);
    setCompletionError(null);
    try {
      await api.post('/auth/complete-onboarding', {
        birthYear: formData.birthYear,
        diabetesDiagnosisYear: formData.diabetesDiagnosisYear,
        diabetesType: formData.diabetesType,
        country: formData.country,
        timezone: formData.timezone,
        preferredUnit: formData.preferredUnit,
        language: formData.language,
      });
      setCompletionSuccess(true);
      setTimeout(() => handleComplete(), 1500);
    } catch (err) {
      console.error('Failed to complete onboarding:', err);
      setCompletionError(err.response?.data?.error || t('onboarding.complete.submitError'));
    } finally {
      setLoading(false);
    }
  };

  // Get step labels based on mode
  const getStepLabels = () => {
    if (mode === 'profile-completion') {
      return [
        t('onboarding.personal.title'),
        t('onboarding.preferences.title'),
        t('onboarding.complete.title'),
      ];
    }
    return [
      t('onboarding.subscription.title'),
      t('onboarding.personal.title'),
      t('onboarding.preferences.title'),
      t('onboarding.complete.title'),
    ];
  };

  // Map step index for profile completion mode
  const getAdjustedStep = () => {
    if (mode === 'profile-completion') {
      return activeStep - 1;
    }
    return activeStep;
  };

  // Button rendering for footer
  const renderBackButton = () => {
    // Hide on first step
    if (activeStep === 0 && mode === 'new-user') return <Box />;
    if (activeStep === 1 && mode === 'profile-completion') return <Box />;

    return (
      <Button onClick={handleBack} variant="outlined" disabled={loading}>
        {t('common.back')}
      </Button>
    );
  };

  const renderNextButton = () => {
    switch (activeStep) {
      case 0: // Subscription - handled internally
        return null;
      case 1: // Personal Info
        return (
          <Button
            onClick={handleNext}
            variant="contained"
            disabled={loading || !validationState.isValid}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : t('common.next')}
          </Button>
        );
      case 2: // Preferences
        return (
          <Button
            onClick={handleNext}
            variant="contained"
            disabled={loading || !validationState.isValid}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : t('common.next')}
          </Button>
        );
      case 3: // Completion
        return (
          <Button
            onClick={handleCompletionSubmit}
            variant="contained"
            disabled={loading}
            size="large"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
          >
            {loading ? t('common.saving') : t('onboarding.complete.getStarted')}
          </Button>
        );
      default:
        return null;
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <SubscriptionStep
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
          />
        );
      case 1:
        return (
          <PersonalInfoStep
            formData={formData}
            updateFormData={updateFormData}
            onValidationChange={(isValid) => setValidationState({ isValid })}
          />
        );
      case 2:
        return (
          <PreferencesStep
            formData={formData}
            updateFormData={updateFormData}
            onValidationChange={(isValid) => setValidationState({ isValid })}
          />
        );
      case 3:
        return (
          <CompletionStep
            formData={formData}
            mode={mode}
            success={completionSuccess}
            error={completionError}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          bgcolor: 'background.paper',
          minHeight: isMobile ? '100%' : '85vh',
        },
      }}
    >
      <DialogContent
        dividers
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          height: isMobile ? '100vh' : '85vh',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
            background: 'linear-gradient(135deg, rgba(144, 202, 249, 0.1) 0%, rgba(33, 37, 41, 0.5) 100%)',
          }}
        >
          <Typography variant="h5" fontWeight={600} gutterBottom>
            {mode === 'profile-completion'
              ? t('onboarding.profileCompletion.title')
              : t('onboarding.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {mode === 'profile-completion'
              ? t('onboarding.profileCompletion.subtitle')
              : t('onboarding.subtitle')}
          </Typography>

          {/* Desktop: Full stepper */}
          {!isMobile && (
            <Box sx={{ mt: 3 }}>
              <Stepper
                activeStep={getAdjustedStep()}
                alternativeLabel
                sx={{
                  '& .MuiStepLabel-label': {
                    fontSize: '0.85rem',
                  },
                  '& .MuiStepIcon-root.Mui-active': {
                    color: 'primary.main',
                  },
                  '& .MuiStepIcon-root.Mui-completed': {
                    color: 'success.main',
                  },
                }}
              >
                {getStepLabels().map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          )}

          {/* Mobile: Simple progress indicator */}
          {isMobile && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Typography variant="body2" color="primary" fontWeight={600}>
                {t('onboarding.wizard.step', {
                  current: getAdjustedStep() + 1,
                  total: getStepLabels().length,
                })}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Content */}
        <Box sx={{ flexGrow: 1, p: 3, overflow: 'auto', minHeight: 0 }}>
          {renderStepContent()}
        </Box>
      </DialogContent>

      {/* Footer with navigation buttons */}
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        {renderBackButton()}
        {renderNextButton()}
      </DialogActions>
    </Dialog>
  );
}

export default OnboardingWizard;
